import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { client, urlFor } from '../lib/sanity'
import { Post, Author } from '../types/sanity'
import BlogPostCard from './BlogPostCard'
import arrow from '../assets/icons/arrow-left-black.svg'
import { Link } from 'react-router-dom'

export default function AuthorPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [author, setAuthor] = useState<Author | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [recommendedAuthors, setRecommendedAuthors] = useState<Author[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const query = `{
      "author": *[_type == "author" && slug.current == $slug][0],
      "posts": *[_type == "post" && author->slug.current == $slug] | order(publishedAt desc),
      "recommendedAuthors": *[_type == "author" && slug.current != $slug] | order(name asc)[0...4]
    }`

    client
      .fetch(query, { slug })
      .then((data: { author: Author; posts: Post[]; recommendedAuthors: Author[] }) => {
        setAuthor(data.author)
        setPosts(data.posts)
        setRecommendedAuthors(data.recommendedAuthors)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching author data:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [slug])

  const handleGoBack = () => {
    navigate(-1)
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (!author) {
    return <div className="text-center py-8">Author not found</div>
  }

  return (
    <div className="w-screen flex justify-center mt-20 mb-32">
      {/* Back button - hidden on mobile */}
      <button 
        onClick={handleGoBack}
        className="hidden md:flex fixed top-24 left-8 z-10 w-16 h-16 bg-white rounded-full border border-gray-200 flex items-center justify-center hover:border-gray-300 transition-colors"
      >
        <img src={arrow} alt="←" className="w-10 h-10" />
      </button>

      <div className="w-full max-w-[1280px] px-8">
        <div className="mb-16 text-center">
          {author.image && (
            <img
              src={urlFor(author.image).width(200).height(200).url()}
              alt={author.name}
              className="w-48 h-48 rounded-full mx-auto mb-8 object-cover"
            />
          )}
          <h1 className="headline-large mb-8">{author.name}</h1>
          {author.bio && <p className="body text-gray-600 max-w-2xl mx-auto mb-8">{author.bio}</p>}
          
          {/* Social Media Icons */}
          <div className="flex justify-center gap-8">
            {author.twitter && (
              <a href={author.twitter} target="_blank" rel="noopener noreferrer" className="text-black hover:text-gray-400 transition-colors duration-300">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="sr-only">Twitter</span>
              </a>
            )}
            {author.linkedin && (
              <a href={author.linkedin} target="_blank" rel="noopener noreferrer" className="text-black hover:text-gray-400 transition-colors duration-300">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
                </svg>
                <span className="sr-only">LinkedIn</span>
              </a>
            )}
            {author.instagram && (
              <a href={author.instagram} target="_blank" rel="noopener noreferrer" className="text-black hover:text-gray-400 transition-colors duration-300">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.25a1.25 1.25 0 1 0-2.5 0 1.25 1.25 0 0 0 2.5 0zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
                </svg>
                <span className="sr-only">Instagram</span>
              </a>
            )}
          </div>
        </div>

        <div className="w-full h-px bg-gray-200 mb-16"></div>

        {posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {posts.map((post) => (
              <BlogPostCard key={post._id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <h2 className="headline-medium mb-4">No hay posts aún</h2>
            <p className="body text-gray-600">
              {author.name} no ha publicado ningún artículo todavía. ¡Vuelve pronto para ver sus contribuciones!
            </p>
          </div>
        )}

        {recommendedAuthors.length > 0 && (
          <>
            <div className="w-full h-px bg-gray-200 my-16"></div>
            
            <div>
              <h2 className="headline-large mb-12">Otros autores</h2>
              <div className="divide-y divide-gray-200">
                {recommendedAuthors.map((recommendedAuthor) => (
                  <Link 
                    key={recommendedAuthor._id}
                    to={`/autor/${recommendedAuthor.slug.current}`}
                    className="block no-underline transition-all duration-300 hover:bg-white hover:shadow-sm rounded-lg"
                  >
                    <div className="p-6 flex items-center gap-6">
                      <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 md:ml-0 -ml-6">
                        {recommendedAuthor.image ? (
                          <img
                            src={urlFor(recommendedAuthor.image).width(100).height(100).url()}
                            alt={recommendedAuthor.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xl">📸</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-grow">
                        <h3 className="headline-small mb-1 text-gray-900">{recommendedAuthor.name}</h3>
                        <p className="body-small text-gray-600 line-clamp-2">
                          {recommendedAuthor.bio || 'No bio available'}
                        </p>
                      </div>
                      <div className="flex-shrink-0 px-6 py-2 bg-primary text-white rounded-full group-hover:bg-primary/90 transition-colors duration-300">
                        Ver Perfil
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
} 